/**
 * 📄 **보고서용 집계 CSV** — [📊 방문자수] 에서 고른 기간의 숫자를 그대로 내려받는다 (2026-10-09).
 *
 * 결과보고·조치계획서 ■10 칸을 채울 때 화면 숫자를 손으로 옮겼다. 옮기다 **누계를 기간 값으로**
 * 적는 일이 QR 집계에서 실제로 있었다 — 그래서 파일 첫 줄에 **어느 기간 숫자인지**를 박는다.
 *
 * 비식별 집계뿐이다(불변식 1) — 서버에 있는 것이 「몇 번」뿐이라 파일에도 그것뿐이다.
 * 엑셀에서 한글이 안 깨지게 BOM 을 붙이고, 칸 안의 쉼표·따옴표·줄바꿈은 따옴표로 감싼다.
 */
export interface StatRow {
  day: string;
  name: string;
  key: string;
  n: number;
}

/** 엑셀이 수식으로 읽는 글머리(=,+,-,@)는 글자로 막는다 — 바깥에서 온 키가 칸에 들어간다(불변식 7) */
function cell(v: string | number): string {
  let s = String(v);
  if (typeof v === "string" && /^[=+\-@]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

const line = (xs: (string | number)[]) => xs.map(cell).join(",");

export function statsCsv(
  rows: readonly StatRow[],
  totals: Readonly<Record<string, number>>,
  labelOf: (name: string) => string,
  period: string,
): string {
  const out: string[] = [];
  out.push(line(["기간", period]));
  out.push(line(["주의", "횟수입니다(같은 아이가 두 번 들어오면 2). 누가 왔는지는 저장하지 않습니다."]));
  // 🔴 날짜는 UTC 로 나뉜다 — 보고서에 옮길 때 한국 날짜로 읽으면 새벽 쓰임이 하루 밀린다(2026-10-11)
  out.push(line(["날짜 기준", "세계 표준시(UTC) — 한국 시각 오전 9시에 날짜가 바뀝니다. 새벽 0~9시 기록은 전날에 들어 있습니다."]));
  out.push("");
  out.push(line(["합계", "이름", "횟수"]));
  for (const [k, n] of Object.entries(totals).sort((a, b) => b[1] - a[1])) out.push(line([k, labelOf(k), n]));
  out.push("");
  out.push(line(["날짜", "종류", "이름", "무엇", "횟수"]));
  const sorted = [...rows].sort((a, b) => a.day.localeCompare(b.day) || a.name.localeCompare(b.name) || b.n - a.n);
  for (const r of sorted) out.push(line([r.day, r.name, labelOf(r.name), r.key, r.n]));
  return "﻿" + out.join("\r\n") + "\r\n";
}

/** 파일 이름 — 기간이 이름에 들어가야 내려받은 파일끼리 안 헷갈린다. 파일 이름에 못 쓰는 글자는 뺀다 */
export function statsCsvName(period: string): string {
  return `방문집계_${period.replace(/[\\/:*?"<>|]+/g, "").replace(/\s+/g, "_")}.csv`;
}
